import type { TFunction } from 'i18next'
import { type DashboardActivationKey, type DashboardUserProfile } from '@services/firebase'

export const LEARNER_ACCESS_EXPIRING_SOON_DAYS = 14
export const LEARNER_ACCESS_TRIAL_ENDING_SOON_DAYS = 3

const DAY_MS = 24 * 60 * 60 * 1000

export type LearnerAccessState =
  | 'ACTIVE'
  | 'EXPIRED'
  | 'EXPIRING_SOON'
  | 'FREE'
  | 'INACTIVE'
  | 'LOCKED'
  | 'TRIAL'

export type LearnerAccessTone = 'danger' | 'success' | 'warning'

export interface LearnerAccessStatus {
  readonly activationKey: DashboardActivationKey | null
  readonly daysRemaining: number | null
  readonly expiredDaysAgo: number | null
  readonly expiresAt: number | null
  readonly hasActiveAccess: boolean
  readonly hasActivationRecord: boolean
  readonly label: string
  readonly noticeBody: string
  readonly noticeTitle: string
  readonly progressPercent: number | null
  readonly remainingLabel: string | null
  readonly state: LearnerAccessState
  readonly statusTitle: string
  readonly tone: LearnerAccessTone
}

type Translate = TFunction<'translation', undefined>

export function deriveLearnerAccessStatus({
  locale,
  now = Date.now(),
  profile,
  t,
}: {
  readonly locale?: string
  readonly now?: number
  readonly profile: DashboardUserProfile | null | undefined
  readonly t?: Translate
}): LearnerAccessStatus {
  const tr = (key: string, options?: Record<string, unknown>) =>
    t ? t(key, { defaultValue: key, ...options }) : interpolate(key, options)
  const activationKey = profile?.activation_key ?? null

  if (isLockedProfile(profile)) {
    return createStatus({
      activationKey,
      label: tr('Account locked'),
      noticeBody: tr('Contact support or use another activation key to restore protected lessons and books.'),
      noticeTitle: tr('Account access is locked'),
      now,
      state: 'LOCKED',
      statusTitle: tr('Protected course access is currently locked.'),
      tone: 'danger',
      tr,
    })
  }

  if (!activationKey) {
    return createStatus({
      activationKey,
      label: tr('Free account'),
      noticeBody: tr('Protected lessons and books require an active key. Buy a key or enter an activation key to unlock full course access.'),
      noticeTitle: tr('Unlock full course access'),
      now,
      state: 'FREE',
      statusTitle: tr('Your account is currently on free access.'),
      tone: 'danger',
      tr,
    })
  }

  if (isActivationExpired(activationKey, now)) {
    return createStatus({
      activationKey,
      label: tr('Expired membership'),
      noticeBody: tr('Course access expired on {{date}}. Buy a new key or enter an activation key to restore protected lessons and books.', {
        date: formatLearnerAccessDate(activationKey.endAt, locale),
      }),
      noticeTitle: tr('Course access has expired'),
      now,
      state: 'EXPIRED',
      statusTitle: tr('Your protected course access has expired.'),
      tone: 'danger',
      tr,
    })
  }

  if (!isActivationMarkedActive(activationKey) || !hasFutureEndAt(activationKey, now)) {
    return createStatus({
      activationKey,
      label: tr('Inactive membership'),
      noticeBody: tr('Enter a valid activation key or buy a plan to restore protected lessons and books.'),
      noticeTitle: tr('Activation needs attention'),
      now,
      state: 'INACTIVE',
      statusTitle: tr('Your membership is not active yet.'),
      tone: 'warning',
      tr,
    })
  }

  if (activationKey.type === 'trial') {
    const trialDaysRemaining = getDaysRemaining(activationKey.endAt, now)
    const isTrialEndingSoon =
      typeof trialDaysRemaining === 'number' &&
      trialDaysRemaining <= LEARNER_ACCESS_TRIAL_ENDING_SOON_DAYS

    return createStatus({
      activationKey,
      label: isTrialEndingSoon ? tr('Trial ending soon') : tr('Free trial'),
      noticeBody: isTrialEndingSoon
        ? tr('Your free trial ends {{when}}. Buy a key or enter an activation key to keep protected lessons and books available.', {
            when: formatRelativeEndCopy(trialDaysRemaining ?? 0, tr),
          })
        : tr('Your free trial unlocks all protected lessons and books.'),
      noticeTitle: isTrialEndingSoon ? tr('Your free trial is ending soon') : tr('Free trial active'),
      now,
      state: 'TRIAL',
      statusTitle: isTrialEndingSoon
        ? tr('Your free trial access is ending soon.')
        : tr('Your free trial gives you full course access.'),
      tone: isTrialEndingSoon ? 'warning' : 'success',
      tr,
    })
  }

  const daysRemaining = getDaysRemaining(activationKey.endAt, now)

  if (
    typeof daysRemaining === 'number' &&
    daysRemaining <= LEARNER_ACCESS_EXPIRING_SOON_DAYS
  ) {
    return createStatus({
      activationKey,
      label: tr('Expiring soon'),
      noticeBody: tr('Your current access ends {{when}}. Buy a key now so protected lessons and books stay available.', {
        when: formatRelativeEndCopy(daysRemaining, tr),
      }),
      noticeTitle: tr('Your plan is ending soon'),
      now,
      state: 'EXPIRING_SOON',
      statusTitle: tr('Your protected course access is active, but ending soon.'),
      tone: 'warning',
      tr,
    })
  }

  return createStatus({
    activationKey,
    label: tr('Premium membership'),
    noticeBody: tr('Your protected course access is active.'),
    noticeTitle: tr('Premium access is active'),
    now,
    state: 'ACTIVE',
    statusTitle: tr('Your protected course access is active.'),
    tone: 'success',
    tr,
  })
}

export function formatLearnerAccessDate(
  value: number | null | undefined,
  locale?: string,
  t?: Translate,
) {
  if (!value) {
    return t ? t('Unknown', { defaultValue: 'Unknown' }) : 'Unknown'
  }

  return new Intl.DateTimeFormat(locale || 'en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value))
}

function interpolate(key: string, options?: Record<string, unknown>) {
  if (!options) {
    return key
  }

  return Object.entries(options).reduce(
    (text, [name, optionValue]) => text.replaceAll(`{{${name}}}`, String(optionValue)),
    key,
  )
}

function createStatus({
  activationKey,
  label,
  noticeBody,
  noticeTitle,
  now,
  state,
  statusTitle,
  tone,
  tr,
}: {
  readonly activationKey: DashboardActivationKey | null
  readonly label: string
  readonly noticeBody: string
  readonly noticeTitle: string
  readonly now: number
  readonly state: LearnerAccessState
  readonly statusTitle: string
  readonly tone: LearnerAccessTone
  readonly tr: (key: string, options?: Record<string, unknown>) => string
}): LearnerAccessStatus {
  const daysRemaining = getDaysRemaining(activationKey?.endAt, now)
  const expiredDaysAgo = getExpiredDaysAgo(activationKey?.endAt, now)

  return {
    activationKey,
    daysRemaining,
    expiredDaysAgo,
    expiresAt: activationKey?.endAt ?? null,
    hasActiveAccess: state === 'ACTIVE' || state === 'EXPIRING_SOON' || state === 'TRIAL',
    hasActivationRecord: Boolean(activationKey),
    label,
    noticeBody,
    noticeTitle,
    progressPercent: getProgressPercent(activationKey, now),
    remainingLabel: getRemainingLabel({ daysRemaining, expiredDaysAgo, state, tr }),
    state,
    statusTitle,
    tone,
  }
}

function isLockedProfile(profile: DashboardUserProfile | null | undefined) {
  return Boolean(
    profile &&
      ((profile as { locked?: unknown }).locked === true ||
        (profile as { status?: unknown }).status === false ||
        (profile as { status?: unknown }).status === 'locked'),
  )
}

function isActivationExpired(activationKey: DashboardActivationKey, now: number) {
  return (
    activationKey.status === 'expired' ||
    (typeof activationKey.endAt === 'number' && activationKey.endAt <= now)
  )
}

function isActivationMarkedActive(activationKey: DashboardActivationKey) {
  if (activationKey.status) {
    return activationKey.status === 'active'
  }

  return activationKey.active === true || activationKey.active === 'activated'
}

function hasFutureEndAt(activationKey: DashboardActivationKey, now: number) {
  return typeof activationKey.endAt === 'number' && activationKey.endAt > now
}

function getDaysRemaining(endAt: number | undefined, now: number) {
  if (typeof endAt !== 'number') {
    return null
  }

  return Math.max(0, Math.ceil((endAt - now) / DAY_MS))
}

function getExpiredDaysAgo(endAt: number | undefined, now: number) {
  if (typeof endAt !== 'number' || endAt > now) {
    return null
  }

  return Math.max(0, Math.floor((now - endAt) / DAY_MS))
}

function getProgressPercent(
  activationKey: DashboardActivationKey | null,
  now: number,
) {
  if (!activationKey?.startAt || !activationKey.endAt) {
    return null
  }

  const totalMs = Math.max(activationKey.endAt - activationKey.startAt, DAY_MS)
  const elapsedMs = Math.min(Math.max(now - activationKey.startAt, 0), totalMs)

  return Math.round((elapsedMs / totalMs) * 100)
}

function getRemainingLabel({
  daysRemaining,
  expiredDaysAgo,
  state,
  tr,
}: {
  readonly daysRemaining: number | null
  readonly expiredDaysAgo: number | null
  readonly state: LearnerAccessState
  readonly tr: (key: string, options?: Record<string, unknown>) => string
}) {
  if (state === 'EXPIRED') {
    if (expiredDaysAgo === null || expiredDaysAgo === 0) {
      return tr('Expired today')
    }

    return tr('Expired {{days}} days ago', { count: expiredDaysAgo, days: expiredDaysAgo })
  }

  if (daysRemaining === null) {
    return null
  }

  if (daysRemaining <= 0) {
    return tr('Expires today')
  }

  return tr('{{days}} days remaining', { count: daysRemaining, days: daysRemaining })
}

function formatRelativeEndCopy(
  daysRemaining: number,
  tr: (key: string, options?: Record<string, unknown>) => string,
) {
  if (daysRemaining <= 0) {
    return tr('today')
  }

  return tr('in {{days}} days', { count: daysRemaining, days: daysRemaining })
}
